import { RollbackOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import { Button, Drawer } from 'antd';
import React, { Suspense, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import styled from 'styled-components';
import { useSetUserCardId } from '../../hooks/useSetUserCardId';
import { useSystemStore } from '../../stores/SystemStore';
import { mobileEvent } from '../../utils/mobile';
import BasicTransition from '../BasicTransition';
import CarLoader from '../CarLoader';
import Center from '../Center';
import ErrorComponent from '../ErrorComponent';
import { Header } from '../Header/Header';
import { Menu } from '../Menu/Menu';
import NotificationsModal from '../NotificationsModal';
import Push from '../Push';
import NewStudentCard from '../StudentCard/NewStudentCard';
import UserUnusedInvites from '../UserUnusedInvites';
import WelcomeSuccessModal from '../WelcomeSuccessModal';
import { InAppLayoutContainer, MainContentLayout } from './styles';

const StyledSwipeButton = styled.div<{ $opacity: number }>`
	pointer-events: none;
	top: 50%;
	left: 5%;
	position: fixed;
	z-index: 10001;
	opacity: ${({ $opacity }) => `${$opacity}%`};

	transition-duration: 1s;
	transition-property: transform;
	transform: ${({ $opacity }) => `${$opacity >= 100 ? 'rotate(-80deg)' : 'none'}`};

	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
`;

const ShadowSwipeArea = styled.div<{ $opacity: number }>`
	pointer-events: none;
	top: calc(50% - 180px);
	left: -200px;
	position: fixed;
	z-index: 10000;

	opacity: ${({ $opacity }) => {
		const max = 20;
		const o = $opacity / 5;

		if (o <= max) {
			return `${o}%`;
		}
		return `${max}%`;
	}};
	height: 400px;
	width: 200px;
	background-color: rgb(61, 107, 177);
	border-radius: 50%;

	/* transition-duration: 1s; */
	transition-property: transform;
	transform: ${({ $opacity }) => `translateX(${(Math.min($opacity, 100) / 100) * 50}px)`};

	@keyframes spin {
		from {
			transform: translateX(0);
		}
		to {
			transform: translateX(50px);
		}
	}
`;

const InAppLayout: React.FC = () => {
	const navigate = useNavigate();
	const [swipePoint, setSwipePoint] = useState(0);
	const fired = React.useRef(false);
	const swipeHandler = useSwipeable({
		onSwiping: e => {
			if (e.deltaX > 0) {
				const cal = e.absX / 2;
				setSwipePoint(cal);

				if (cal > 100 && !fired.current) {
					fired.current = true;
					mobileEvent({ type: 'HapticImpact', style: 'LIGHT' });
				}

				if (fired.current && cal < 90) {
					fired.current = false;
				}
			}
		},
		onSwiped: () => {
			if (swipePoint > 100) {
				navigate(-1);
			}
			fired.current = false;
			setSwipePoint(0);
		},
	});

	const welcomeSuccessModalVisible = useSystemStore(state => state.welcomeSuccessModalVisible);

	const drawerStudentId = useSystemStore(state => state.drawerStudentId);
	const setUserCardId = useSetUserCardId();

	return (
		<>
			<StyledSwipeButton $opacity={swipePoint}>
				<Button icon={<RollbackOutlined />} shape="circle" size="large" type="primary" />
			</StyledSwipeButton>
			<ShadowSwipeArea $opacity={swipePoint} />

			<Drawer placement="right" onClose={() => setUserCardId(undefined)} open={!!drawerStudentId} size="large">
				{drawerStudentId && <NewStudentCard id={drawerStudentId} showLink />}
			</Drawer>
			<WelcomeSuccessModal isOpened={welcomeSuccessModalVisible} />
			<InAppLayoutContainer {...swipeHandler}>
				<UserUnusedInvites />
				<Header />
				<Menu />
				<Push />
				<NotificationsModal />
				<MainContentLayout id="in-app-layout-container">
					<Sentry.ErrorBoundary fallback={<ErrorComponent />}>
						<Suspense
							fallback={
								<BasicTransition>
									<Center>
										<CarLoader size={1} />
									</Center>
								</BasicTransition>
							}
						>
							<Outlet />
						</Suspense>
					</Sentry.ErrorBoundary>
				</MainContentLayout>
			</InAppLayoutContainer>
		</>
	);
};

export default InAppLayout;
